import React from 'react';
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

const Panel3D = ({ wall, brick, mortarGap, onlyHalfBricks, setBrickStats }) => {  
  const { length, height } = wall;
  const { dimensions, properties } = brick;
  const { length: brickL, height: brickH, width: thickness, weight } = dimensions;
  const { color, half } = properties;

  const halfData = onlyHalfBricks.find(b => b.id === half);
  const halfLength = halfData ? halfData.dimensions.length : brickL / 2;
  const halfColor = halfData ? halfData.properties.color : color;

  const lengthTextRef = useRef();
  const heightTextRef = useRef();

  const roundTo2 = (num) => Math.round(num * 100) / 100;
  const roundTo3 = (num) => Math.round(num * 1000) / 1000;
  const roundTo4 = (num) => Math.round(num * 10000) / 10000;
  const roundTo5 = (num) => Math.round(num * 100000) / 100000;

  const brickVolume = roundTo5(brickL * brickH * thickness);
  const brickDensity = roundTo5((weight) / brickVolume);
  const mortarDensity = 1800;
  const mortarCostPerCubicMeter = 120;
    
  const brickPrice = brick.prices.reduce((latest, current) => 
    Number(current.fecha) > Number(latest.fecha) ? current : latest
  ).precio;

  const brickPricePerSQMeter = brickPrice/brickVolume;

  const halfBrickPrice = halfData.prices.reduce((latest, current) => 
    Number(current.fecha) > Number(latest.fecha) ? current : latest
  ).precio;

  useFrame(({ camera }) => {
    if (lengthTextRef.current) lengthTextRef.current.lookAt(camera.position);
    if (heightTextRef.current) heightTextRef.current.lookAt(camera.position);
  });

  const { bricks, mortar } = useMemo(() => {
    const _bricks = [];
    const _mortar = [];
    let totalMortarVolume = 0;

    let bricksWeight = 0;
    let brickCost = 0;

    const brickCounts = {
      fullBricks: 0,
      halfBricks: 0,
      incompleteHalfBricks: 0,
      customBricksGroups: {}
    };

    let usableLength = length - mortarGap;
    let usableHeight = height - mortarGap;

    let currentY = -usableHeight / 2;
    let rowCounter = 0;

    const EPSILON = 0.001;

    const getBrickKey = (sizeX, sizeY) => {
      return `${roundTo3(sizeX)}x${roundTo3(sizeY)}x${roundTo3(thickness)}`;
    };

    const bottomTopMortarVolume = (usableLength + mortarGap * 2) * mortarGap * thickness;
    const sideMortarVolume = mortarGap * (usableHeight + mortarGap * 2) * thickness;
    
    _mortar.push(
      <mesh key="mortar-bottom" position={[0, -usableHeight / 2 - mortarGap / 2, 0]}>
        <boxGeometry args={[usableLength + mortarGap * 2, mortarGap, thickness]} />
        <meshStandardMaterial color="#cccccc" />
      </mesh>,
      <mesh key="mortar-top" position={[0, usableHeight / 2 + mortarGap / 2, 0]}>
        <boxGeometry args={[usableLength + mortarGap * 2, mortarGap, thickness]} />
        <meshStandardMaterial color="#cccccc" />
      </mesh>,
      <mesh key="mortar-left" position={[-usableLength / 2 - mortarGap / 2, 0, 0]}>
        <boxGeometry args={[mortarGap, usableHeight + mortarGap * 2, thickness]} />
        <meshStandardMaterial color="#cccccc" />
      </mesh>,
      <mesh key="mortar-right" position={[usableLength / 2 + mortarGap / 2, 0, 0]}>
        <boxGeometry args={[mortarGap, usableHeight + mortarGap * 2, thickness]} />
        <meshStandardMaterial color="#cccccc" />
      </mesh>
    );

    totalMortarVolume += bottomTopMortarVolume * 2;
    totalMortarVolume += sideMortarVolume * 2;

    while (currentY < usableHeight / 2) {
      const rowHeight = Math.min(brickH, usableHeight / 2 - currentY);
      
      if (rowHeight <= 0) break;

      let currentX = -usableLength / 2;
      const isOddRow = rowCounter % 2 !== 0;
      let bricksInRow = 0;

      while (currentX < usableLength / 2 - EPSILON) {
        const remainingX = usableLength / 2 - currentX;
        let brickSizeX = brickL;
        let brickColor = color;
        let brickType = 'full';

        if (isOddRow && halfData && bricksInRow === 0 && remainingX >= halfLength) {
          brickSizeX = halfLength;
          brickColor = halfColor;
          brickType = 'half';
        } 
        else if (remainingX < brickL) {
          brickSizeX = remainingX;
          if (halfData && Math.abs(remainingX - halfLength) < 0.001) {
            brickSizeX = halfLength;
            brickColor = halfColor;
            brickType = 'half';
          } 
          else {
            brickType = 'custom';
          }
        }

        if (currentX + brickSizeX > usableLength / 2) {
          brickSizeX = usableLength / 2 - currentX;
          brickType = 'custom';
        }

        if (roundTo2(rowHeight) < brickH) {
          brickType = 'custom';
        }

        if (brickSizeX > 0.01) {
          const posX = currentX + brickSizeX / 2;
          const posY = currentY + rowHeight / 2;

          _bricks.push(
            <group key={`${rowCounter}-${bricksInRow}`}>
              <mesh position={[posX, posY, 0]}>
                <boxGeometry args={[brickSizeX, rowHeight, thickness]} />
                <meshStandardMaterial color={brickColor} />
              </mesh>
              <lineSegments position={[posX, posY, 0]}>
                <edgesGeometry args={[new THREE.BoxGeometry(brickSizeX, rowHeight, thickness)]} />
                <lineBasicMaterial color="#111111" />
              </lineSegments>
            </group>
          );

          if (brickType === 'full') {
            brickCounts.fullBricks++;
            bricksWeight += roundTo4(brickDensity * rowHeight * brickSizeX * thickness);
            brickCost += brickPrice;
          } 
          else if (brickType === 'half') {
            brickCounts.halfBricks++;
            bricksWeight += roundTo4(brickDensity * rowHeight * brickSizeX * thickness);
            brickCost += halfBrickPrice;
          }
          else {            
            const brickKey = getBrickKey(brickSizeX, rowHeight);
            const isSmallerThanHalf = halfData && (brickSizeX < halfLength);
            const brickVolume = rowHeight * brickSizeX * thickness
            bricksWeight += roundTo4(brickDensity * brickVolume);
            brickCost += brickPricePerSQMeter * brickVolume;              
            
            if (isSmallerThanHalf) {
              brickCounts.incompleteHalfBricks++;
            }

            if (!brickCounts.customBricksGroups[brickKey]) {
              brickCounts.customBricksGroups[brickKey] = {
                count: 0,
                dimensions: {
                  length: roundTo3(brickSizeX * 100),
                  height: roundTo3(rowHeight * 100),
                  thickness: roundTo3(thickness * 100),
                  type: isSmallerThanHalf ? 'medio ladrillo cortado' : 'ladrillo cortado'
                }
              };
            }
            brickCounts.customBricksGroups[brickKey].count++;
          }

          currentX += brickSizeX;
          bricksInRow++;

          if (currentX < usableLength / 2) {
            const verticalMortarVolume = mortarGap * rowHeight * thickness;
            totalMortarVolume += verticalMortarVolume;
            
            _mortar.push(
              <mesh key={`mortar-${rowCounter}-${bricksInRow}`} position={[currentX + mortarGap / 2, posY, 0]}>
                <boxGeometry args={[mortarGap, rowHeight, thickness]} />
                <meshStandardMaterial color="#cccccc" />
              </mesh>
            );
            currentX += mortarGap;
          }
        }
      }

      currentY += rowHeight;

      if (currentY < usableHeight / 2) {
        const horizontalMortarVolume = usableLength * mortarGap * thickness;
        totalMortarVolume += horizontalMortarVolume;
        
        _mortar.push(
          <mesh key={`mortar-h-${rowCounter}`} position={[0, currentY + mortarGap / 2, 0]}>
            <boxGeometry args={[usableLength, mortarGap, thickness]} />
            <meshStandardMaterial color="#cccccc" />
          </mesh>
        );
        currentY += mortarGap;
      }

      rowCounter++;
    }

    const customBricksArray = Object.values(brickCounts.customBricksGroups)
      .sort((a, b) => b.count - a.count);

    const stats = {
      totalMortarVolume: roundTo3(totalMortarVolume),
      brickCounts: {
        fullBricks: brickCounts.fullBricks,
        halfBricks: brickCounts.halfBricks,
        incompleteHalfBricks: brickCounts.incompleteHalfBricks,
        customBricks: customBricksArray
      },
      totalWeight: roundTo2((bricksWeight + totalMortarVolume * mortarDensity)),
      linearWeight: roundTo2((bricksWeight + totalMortarVolume * mortarDensity)/ length),
      mortarCost: roundTo2(mortarCostPerCubicMeter * totalMortarVolume),
      bricksCost: roundTo2(brickCost),
    };
    
    setBrickStats(stats);

    return { bricks: _bricks, mortar: _mortar };
  }, [wall, brick, mortarGap, onlyHalfBricks, setBrickStats]);

  return (
    <group castShadow>
      {/* Piso simple y visible */}
      <mesh 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, -height/2 - 0.005, -thickness/2]}
        receiveShadow
      >
        <planeGeometry args={[length * 3, length * 3]} receiveShadow />
        <meshStandardMaterial 
          color="#e0e0e0"
          side={THREE.DoubleSide}
          roughness={0.8}
          metalness={0.1}
        />
      </mesh>
      
      <group ref={lengthTextRef} position={[0, + height/2 + mortarGap + 0.15, 0]}>
        <Text color="black" anchorX="center" anchorY="middle" fontSize={0.1}>
          {length.toFixed(2)} m
        </Text>
      </group>
      
      <group ref={heightTextRef} position={[-length/2 - mortarGap - 0.15, 0, 0]}>
        <Text 
          color="black" 
          anchorX="center" 
          anchorY="middle" 
          fontSize={0.1}
          rotation={[0, 0, Math.PI/2]}
        >
          {height.toFixed(2)} m
        </Text>
      </group>
      
      {mortar}
      {bricks}
    </group>
  );
};

export default Panel3D;